/**
 * Utility functions for handling WebView event communication between web and React Native
 */

import { WEB_VIEW_APP_EVENTS, IdleTimerStage } from "./webApputils/analyticEvents";

/**
 * Type definition for WebView event data
 */
export interface WebViewEventData {
  type: string;
  payload?: any;
}

type EventHandler = (payload?: any) => void;
type EventHandlers = { [key: string]: EventHandler[] };

// Store for event handlers
const eventHandlers: EventHandlers = {};

/**
 * Initialize the WebView event listener
 * This should be called once when your app starts
 */
export const initWebViewEventListener = (): void => {
  if (typeof window === "undefined") return;

  window.addEventListener("message", (event: MessageEvent) => {
    try {
      // Handle both string and object messages
      const data: WebViewEventData =
        typeof event.data === "string" ? JSON.parse(event.data) : event.data;

      if (data && typeof data === "object" && "type" in data) {
        const handlers = eventHandlers[data.type] || [];
        handlers.forEach((handler) => handler(data.payload));
      }
    } catch (error) {
      console.error("Error handling WebView message:", error, "Event data:", event.data);
    }
  });
};

/**
 * Subscribe to events from React Native
 * @param type - The type of event to listen for
 * @param handler - The function to call when the event is received
 * @returns A function to unsubscribe from the event
 */
export const subscribeToWebViewEvent = (type: string, handler: EventHandler): (() => void) => {
  if (!eventHandlers[type]) {
    eventHandlers[type] = [];
  }
  eventHandlers[type].push(handler);

  // Return unsubscribe function
  return () => {
    eventHandlers[type] = eventHandlers[type].filter((h) => h !== handler);
  };
};

/**
 * Low-level bridge send. Delivers a serialized message to the native shell,
 * transparently supporting both hosts:
 * - iOS (WKWebView): `window.webkit.messageHandlers.amberBridge.postMessage`
 * - Android / RN WebView: `window.ReactNativeWebView.postMessage`
 *
 * The iOS native shell also aliases `window.ReactNativeWebView.postMessage` to the
 * same handler, so either path works; the WebKit handler is preferred when present.
 * @param message - The already-serialized (JSON string) message to send
 */
export const sendNativeBridge = (message: string): void => {
  if (typeof window === "undefined") return;

  const iosHandler = window.webkit?.messageHandlers?.amberBridge;
  if (iosHandler && typeof iosHandler.postMessage === "function") {
    iosHandler.postMessage(message);
    return;
  }

  if (window.ReactNativeWebView?.postMessage) {
    window.ReactNativeWebView.postMessage(message);
  }
};

/**
 * Returns true when a native host is present, so the bridge can be used.
 *
 * `window.isApp` is set asynchronously (via cookies during the first render),
 * so it is not reliable at startup — e.g. when an axios interceptor fires before
 * AppLayout has written the flag. The native host handles
 * (`ReactNativeWebView` / `webkit.messageHandlers.amberBridge`) are injected
 * synchronously by the shell before the page loads, so treat them as the
 * authoritative "we are inside the app" signal regardless of cookie state.
 */
const isNativeHostPresent = (): boolean => {
  if (typeof window === "undefined") return false;
  return Boolean(
    window.isApp ||
      window.ReactNativeWebView?.postMessage ||
      window.webkit?.messageHandlers?.amberBridge,
  );
};

/**
 * Sends an event from web to the native shell (iOS WKWebView or RN WebView).
 * No-ops outside the app (guarded on presence of any native host).
 * @param type - The type of event to send
 * @param payload - Optional data to send with the event
 */
export const sendWebViewEvent = (type: string, payload?: any): void => {
  if (!isNativeHostPresent()) return;
  const eventData: WebViewEventData = {
    type,
    payload,
  };
  sendNativeBridge(JSON.stringify(eventData));
};

/**
 * Creates a click handler that sends an event to React Native
 * @param type - The type of event to send on click
 * @param payload - Optional data to send with the event
 * @returns A click event handler function
 */
export const createWebViewClickHandler =
  (type: string, payload?: any) => (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    sendWebViewEvent(type, payload);
  };

// Add TypeScript declarations for the native bridge hosts
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
    // iOS WKWebView message handler installed by the native shell.
    webkit?: {
      messageHandlers?: {
        amberBridge?: {
          postMessage: (message: string) => void;
        };
      };
    };
    isApp?: boolean;
  }
}

// Idle Timer Notification Functions

interface IdleTimerStartPayload {
  stage: IdleTimerStage;
  redirectUrl: string;
}

interface IdleTimerCancelPayload {
  stage: IdleTimerStage;
}

/**
 * Starts the idle timer for a given stage.
 * Call this when the user exits/abandons a form.
 * @param stage - The form stage (lead_form, book_form, payment)
 * @param redirectUrl - The full URL to redirect to when notification is tapped
 */
export function startIdleTimer(stage: IdleTimerStage, redirectUrl: string) {
  const payload: IdleTimerStartPayload = { stage, redirectUrl };
  sendWebViewEvent(WEB_VIEW_APP_EVENTS.IDLE_TIMER_START, payload);
}

/**
 * Cancels the idle timer for a given stage.
 * Call this when the user successfully completes a form.
 */
export function cancelIdleTimer(stage: IdleTimerStage) {
  const payload: IdleTimerCancelPayload = { stage };
  sendWebViewEvent(WEB_VIEW_APP_EVENTS.IDLE_TIMER_CANCEL, payload);
}
